import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ReceiptText, Eye, Download, Paperclip, Upload, Smartphone, Trash2,
  CheckCheck, AlertTriangle, FileText, ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_COLOR = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  sent: "bg-blue-100 text-blue-800 border-blue-200",
  partial: "bg-amber-100 text-amber-900 border-amber-300",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  void: "bg-red-100 text-red-800 border-red-200",
};

const backendBase = process.env.REACT_APP_BACKEND_URL;
const absUrl = (url) => (url?.startsWith("http") ? url : `${backendBase}${url}`);

export default function MyInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proofsOpen, setProofsOpen] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [previewProof, setPreviewProof] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my/invoices");
      setInvoices(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load invoices");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/my/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url; link.download = `invoice_${inv.invoice_number}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const previewPdf = async (inv) => {
    try {
      const r = await api.get(`/my/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch { toast.error("Preview failed"); }
  };

  const uploadProof = async (invoiceId, file) => {
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("File too large (max 12 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/my/invoices/${invoiceId}/payment-proof`, fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
      });
      toast.success("Payment proof attached — your hospital will review it.");
      setInvoices((arr) => arr.map((inv) =>
        inv.id === invoiceId
          ? { ...inv, payment_proofs: [...(inv.payment_proofs || []), data] }
          : inv,
      ));
      setProofsOpen((cur) => (cur && cur.id === invoiceId
        ? { ...cur, payment_proofs: [...(cur.payment_proofs || []), data] }
        : cur));
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6 fade-in" data-testid="my-invoices-page">
      <div>
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>My invoices</h1>
        <p className="text-[#525C58] mt-1">Download your invoices and attach payment proofs (M-PESA / bank slips).</p>
      </div>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ReceiptText className="h-5 w-5 text-[#40916C]" /> All invoices ({invoices.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? <p className="py-10 text-center text-[#7A8A84]">Loading…</p> :
           invoices.length === 0 ? <p className="py-10 text-center text-[#7A8A84]">No invoices yet.</p> : (
            <div className="overflow-x-auto">
              <Table data-testid="my-invoices-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Issued</TableHead>
                    <TableHead>Due</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Paid</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`my-invoice-row-${inv.id}`}>
                      <TableCell className="font-mono">#{inv.invoice_number}</TableCell>
                      <TableCell className="text-sm text-[#525C58]">{(inv.issued_at || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-sm text-[#525C58]">{inv.due_at}</TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {Object.entries(inv.totals || {}).filter(([, v]) => v).map(([k, v]) => (
                          <div key={k}>{k} {v.toLocaleString()}</div>
                        ))}
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {inv.amount_paid ? (
                          <>
                            <div className="text-emerald-700 font-medium">KES {Number(inv.amount_paid).toLocaleString()}</div>
                            {inv.amount_due > 0 && <div className="text-[10px] text-red-600">due {Number(inv.amount_due).toLocaleString()}</div>}
                          </>
                        ) : <span className="text-[#7A8A84]">—</span>}
                      </TableCell>
                      <TableCell><Badge className={`${STATUS_COLOR[inv.status]} border text-xs`}>{inv.status}</Badge></TableCell>
                      <TableCell className="text-right space-x-1 whitespace-nowrap">
                        <Button size="sm" variant="ghost" onClick={() => previewPdf(inv)} className="text-[#1B4332]" title="Preview" data-testid={`my-preview-${inv.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => downloadPdf(inv)} className="text-[#1B4332]" title="Download PDF" data-testid={`my-download-${inv.id}`}>
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setProofsOpen(inv)}
                          className={`relative ${(inv.payment_proofs?.length || 0) > 0 ? "text-indigo-700 hover:bg-indigo-50" : "text-[#525C58] hover:bg-[#F4F4F1]"}`}
                          title={(inv.payment_proofs?.length || 0) > 0 ? `${inv.payment_proofs.length} proof(s) attached` : "Upload payment proof"}
                          data-testid={`my-proofs-${inv.id}`}
                        >
                          <Paperclip className="h-3.5 w-3.5" />
                          {(inv.payment_proofs?.length || 0) > 0 && (
                            <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-indigo-600 text-white text-[9px] font-semibold flex items-center justify-center" data-testid={`my-proofs-count-${inv.id}`}>
                              {inv.payment_proofs.length}
                            </span>
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Proofs dialog (tenant-side — same as admin but with /my/* upload) */}
      <Dialog open={!!proofsOpen} onOpenChange={(o) => !o && setProofsOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="my-proofs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-5 w-5 text-indigo-700" /> Payment proofs
            </DialogTitle>
            <DialogDescription>
              {proofsOpen && (
                <>Invoice #{proofsOpen.invoice_number}. Attach M-PESA screenshots, bank slips or scanned receipts (image or PDF, max 12 MB).</>
              )}
            </DialogDescription>
          </DialogHeader>
          {proofsOpen && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm ${uploading ? "opacity-60 pointer-events-none" : "border-[#1B4332] bg-[#1B4332] text-white hover:bg-[#2D6A4F]"}`} data-testid="my-upload-file-btn">
                  <Upload className="h-4 w-4" />
                  {uploading ? "Uploading…" : "Choose file / PDF"}
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadProof(proofsOpen.id, f); }}
                    disabled={uploading}
                    data-testid="my-upload-file-input"
                  />
                </label>
                <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm ${uploading ? "opacity-60 pointer-events-none" : "border-[#1B4332] text-[#1B4332] hover:bg-[#F4F4F1]"}`} data-testid="my-upload-camera-btn">
                  <Smartphone className="h-4 w-4" />
                  Take photo
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadProof(proofsOpen.id, f); }}
                    disabled={uploading}
                    data-testid="my-upload-camera-input"
                  />
                </label>
              </div>

              {(proofsOpen.payment_proofs?.length || 0) === 0 ? (
                <div className="border border-dashed border-[#E8E8E6] rounded-lg p-8 text-center text-sm text-[#7A8A84]" data-testid="my-proofs-empty">
                  <Paperclip className="h-8 w-8 mx-auto mb-2 text-[#C4C9C7]" />
                  No proofs uploaded yet for this invoice.
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="my-proofs-gallery">
                  {proofsOpen.payment_proofs.map((p) => {
                    const isPdf = p.mime_type === "application/pdf";
                    const isImg = (p.mime_type || "").startsWith("image/");
                    return (
                      <div key={p.id} className="border border-[#E8E8E6] rounded-lg overflow-hidden bg-[#FAFAF8]" data-testid={`my-proof-${p.id}`}>
                        <button type="button" onClick={() => setPreviewProof(p)} className="w-full h-32 flex items-center justify-center bg-white hover:bg-[#F4F4F1]">
                          {isImg ? (
                            <img src={absUrl(p.url)} alt={p.original_filename} className="w-full h-full object-cover" loading="lazy" />
                          ) : isPdf ? (
                            <div className="flex flex-col items-center gap-1 text-red-700">
                              <FileText className="h-10 w-10" /><span className="text-[10px] font-semibold">PDF</span>
                            </div>
                          ) : <ImageIcon className="h-10 w-10 text-[#C4C9C7]" />}
                        </button>
                        <div className="p-2 text-[10px] space-y-0.5">
                          <p className="font-medium truncate" title={p.original_filename}>{p.original_filename}</p>
                          <p className="text-[#7A8A84]">{Math.round((p.size_bytes || 0) / 1024)} KB</p>
                          <p className="text-[#A3ACA8]">{new Date(p.uploaded_at).toLocaleString()}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {proofsOpen.status !== "paid" && (proofsOpen.payment_proofs?.length || 0) > 0 && (
                <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-900" data-testid="my-proof-pending-banner">
                  <CheckCheck className="h-4 w-4 shrink-0" />
                  <span>Thank you — your hospital will review the proof and mark this invoice paid.</span>
                </div>
              )}
              {(proofsOpen.payment_proofs?.length || 0) === 0 && proofsOpen.amount_due > 0 && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  <span>This invoice has an outstanding balance — upload an M-PESA screenshot or bank slip after paying.</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofsOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!previewProof} onOpenChange={(o) => !o && setPreviewProof(null)}>
        <DialogContent className="max-w-3xl" data-testid="my-proof-preview-dialog">
          <DialogHeader>
            <DialogTitle className="truncate pr-8">{previewProof?.original_filename}</DialogTitle>
          </DialogHeader>
          {previewProof && (previewProof.mime_type || "").startsWith("image/") ? (
            <div className="flex items-center justify-center bg-black/5 rounded-lg p-2">
              <img src={absUrl(previewProof.url)} alt={previewProof.original_filename} className="max-h-[70vh] w-auto object-contain rounded" />
            </div>
          ) : previewProof ? (
            <div className="flex flex-col items-center gap-3 py-8">
              <FileText className="h-16 w-16 text-red-700" />
              <Button onClick={() => window.open(absUrl(previewProof.url), "_blank")} className="bg-[#1B4332] hover:bg-[#2D6A4F]">
                <Eye className="h-4 w-4 mr-2" /> Open PDF
              </Button>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewProof(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
